// Events activity feed — the merchant's "is it actually working, and what
// data is flowing?" view. Lists the most recent events Datacrow processed,
// each with the identifiers + commerce it captured and the per-destination
// delivery outcome. Click a row to expand full detail. Backed by
// GET /api/dashboard/activity (cursor-paginated by eventTime).
//
// This is the concrete proof a server-side tracking app needs: you can't
// see server-side traffic in browser devtools, so the health chart's "47
// delivered" is abstract. "Order #1001, $49.99, buyer@example.com → GA4 ✓
// Klaviyo ✓ — 2 min ago" is not.

import { useEffect, useMemo, useState } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Card,
  Text,
  Badge,
  Button,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  Collapsible,
  Box,
  Divider,
  EmptyState,
  Select,
  TextField,
} from "@shopify/polaris";
import { apiFetch, ApiError } from "./api";
import { deliveryNote, finalOutcomes, DESTINATION_NAMES } from "./delivery-note";

// One row per delivery attempt, grouped by destination and oldest first
// (src/lib/activity-feed.ts): a retried or replayed event has several rows
// for one destination. The badges show each destination's final outcome
// (finalOutcomes); the expanded detail lists every attempt.
interface ActivityDelivery {
  destinationType: string;
  status: string; // "success" | "failure" | "skipped" | "dry_run"
  // The worker's machine string; shown through deliveryNote(), never raw.
  errorMessage: string | null;
  attemptedAt: string;
}

interface ActivityEvent {
  eventId: string;
  eventName: string;
  eventTime: string;
  source: string;
  identity: { email: string | null; phone: string | null; shopifyCustomerId: string | null };
  commerce: {
    value: number | null;
    currency: string | null;
    orderId: string | null;
    itemCount: number;
  };
  deliveries: ActivityDelivery[];
}

interface ActivityResponse {
  events: ActivityEvent[];
  nextCursor: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  page_view: "Page view",
  view_item: "Viewed product",
  add_to_cart: "Added to cart",
  begin_checkout: "Began checkout",
  purchase: "Purchase",
  refund: "Refund",
};

// delivery-note already exports exactly this map (the catalog's `name`, the
// form used mid-sentence, which is what an activity row is). Re-deriving it
// here was a second copy of the derivation, which is the duplication this
// branch exists to remove.
const DEST_LABELS = DESTINATION_NAMES;

const EVENT_TYPE_OPTIONS = [
  { label: "All events", value: "" },
  { label: "Page view", value: "page_view" },
  { label: "Viewed product", value: "view_item" },
  { label: "Added to cart", value: "add_to_cart" },
  { label: "Began checkout", value: "begin_checkout" },
  { label: "Purchase", value: "purchase" },
  { label: "Refund", value: "refund" },
];

const TIMEFRAME_OPTIONS = [
  { label: "Last 3 hours", value: "3h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
  { label: "Custom range", value: "custom" },
];

// Resolve a timeframe selection into since/until bounds (UTC instants).
function computeTimeframe(
  timeframe: string,
  customStart: string,
  customEnd: string,
): { since?: Date; until?: Date } {
  const now = Date.now();
  const hours = (h: number) => new Date(now - h * 3600_000);
  switch (timeframe) {
    case "3h":
      return { since: hours(3) };
    case "24h":
      return { since: hours(24) };
    case "7d":
      return { since: hours(24 * 7) };
    case "30d":
      return { since: hours(24 * 30) };
    case "custom":
      return {
        since: customStart ? new Date(`${customStart}T00:00:00`) : undefined,
        until: customEnd ? new Date(`${customEnd}T23:59:59`) : undefined,
      };
    case "all":
    default:
      return {};
  }
}

// The reusable activity feed — filters + event list + load-more, with no
// <Page> chrome so it can be embedded. The dashboard renders it full-width
// below the chart; the standalone <Activity> page (below) wraps it in Page
// chrome. `heading` renders a section title + Refresh button when embedded.
export function ActivityFeed({ heading }: { heading?: string }) {
  const shopify = useAppBridge();
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Filters.
  const [eventType, setEventType] = useState("");
  const [timeframe, setTimeframe] = useState("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const filtersActive =
    eventType !== "" || timeframe !== "all";

  // The base query params for the current filter selection (everything but
  // the pagination cursor). Memoized so the effect below only refetches when
  // a filter actually changes. For "custom" with no dates entered yet, this
  // resolves to no timeframe bounds (i.e. all time) until dates are picked.
  const filterQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (eventType) p.set("eventNames", eventType);
    const { since, until } = computeTimeframe(timeframe, customStart, customEnd);
    if (since) p.set("since", since.toISOString());
    if (until) p.set("until", until.toISOString());
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, timeframe, customStart, customEnd]);

  const load = async (baseQuery: string, before?: string) => {
    try {
      const p = new URLSearchParams(baseQuery);
      if (before) p.set("before", before);
      const qs = p.toString() ? `?${p.toString()}` : "";
      const resp = await apiFetch<ActivityResponse>(
        shopify,
        `/api/dashboard/activity${qs}`,
      );
      setEvents((prev) => (before && prev ? [...prev, ...resp.events] : resp.events));
      setCursor(resp.nextCursor);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    }
  };

  // Refetch fresh whenever the filter selection changes (and on mount).
  useEffect(() => {
    setEvents(null);
    setExpanded(new Set());
    void load(filterQuery);
    // shopify is stable per App Bridge contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const onLoadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    await load(filterQuery, cursor);
    setLoadingMore(false);
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filterBar = (
    <Card>
      <InlineStack gap="300" blockAlign="end" wrap>
        <Select
          label="Event type"
          options={EVENT_TYPE_OPTIONS}
          value={eventType}
          onChange={setEventType}
        />
        <Select
          label="Timeframe"
          options={TIMEFRAME_OPTIONS}
          value={timeframe}
          onChange={setTimeframe}
        />
        {timeframe === "custom" && (
          <>
            <TextField
              label="From"
              type="date"
              value={customStart}
              onChange={setCustomStart}
              autoComplete="off"
            />
            <TextField
              label="To"
              type="date"
              value={customEnd}
              onChange={setCustomEnd}
              autoComplete="off"
            />
          </>
        )}
      </InlineStack>
    </Card>
  );

  let body: React.ReactNode;
  if (error) {
    body = (
      <Banner tone="critical" title="Couldn't load activity">
        <p>{error}</p>
      </Banner>
    );
  } else if (events === null) {
    body = (
      <Card>
        <BlockStack gap="200" align="center">
          <Spinner accessibilityLabel="Loading activity" size="large" />
          <Text as="p" tone="subdued">Loading…</Text>
        </BlockStack>
      </Card>
    );
  } else if (events.length === 0) {
    body = (
      <Card>
        <EmptyState
          heading={filtersActive ? "No events match these filters" : "No events yet"}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>
            {filtersActive
              ? "Try widening the timeframe or clearing the event-type filter."
              : "Once shoppers browse and buy on your store, events show up here in real time, with the data captured and where it was delivered. Place a test order to see the feed populate."}
          </p>
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <BlockStack gap="300">
        <Card padding="0">
          <BlockStack gap="0">
            {events.map((e, i) => (
              <div key={e.eventId}>
                {i > 0 && <Divider />}
                <EventRow
                  event={e}
                  expanded={expanded.has(e.eventId)}
                  onToggle={() => toggle(e.eventId)}
                />
              </div>
            ))}
          </BlockStack>
        </Card>

        {cursor && (
          <InlineStack align="center">
            <Button onClick={onLoadMore} loading={loadingMore}>
              Load more
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        {heading ? (
          <Text as="h2" variant="headingMd">
            {heading}
          </Text>
        ) : (
          <span />
        )}
        <Button
          size="slim"
          onClick={() => {
            setEvents(null);
            void load(filterQuery);
          }}
          disabled={events === null}
        >
          Refresh
        </Button>
      </InlineStack>
      {filterBar}
      {body}
    </BlockStack>
  );
}

function EventRow({
  event,
  expanded,
  onToggle,
}: {
  event: ActivityEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const label = EVENT_LABELS[event.eventName] ?? event.eventName;
  const identityLabel =
    event.identity.email ??
    event.identity.phone ??
    (event.identity.shopifyCustomerId
      ? `Customer ${event.identity.shopifyCustomerId}`
      : "Anonymous");

  return (
    <Box padding="300">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onToggle();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <InlineStack align="space-between" blockAlign="center" wrap={false}>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Badge tone={event.eventName === "refund" ? "warning" : undefined}>
              {label}
            </Badge>
            <BlockStack gap="0">
              <Text as="span" variant="bodyMd">
                {identityLabel}
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {formatRelative(event.eventTime)} · {sourceLabel(event.source)}
                {event.commerce.value !== null
                  ? ` · ${formatMoney(event.commerce.value, event.commerce.currency)}`
                  : ""}
              </Text>
            </BlockStack>
          </InlineStack>
          <InlineStack gap="150" blockAlign="center" wrap>
            {event.deliveries.length === 0 ? (
              <Text as="span" variant="bodySm" tone="subdued">
                pending
              </Text>
            ) : (
              finalOutcomes(event.deliveries).map((d) => (
                <Badge key={d.destinationType} tone={deliveryNote(d.status, d.errorMessage).tone}>
                  {DEST_LABELS[d.destinationType] ?? d.destinationType}
                </Badge>
              ))
            )}
          </InlineStack>
        </InlineStack>
      </div>

      <Collapsible
        id={`detail-${event.eventId}`}
        open={expanded}
        transition={{ duration: "150ms", timingFunction: "ease-in-out" }}
      >
        <Box paddingBlockStart="300">
          <BlockStack gap="200">
            <DetailRow label="Event">{label} ({event.eventName})</DetailRow>
            <DetailRow label="Time">
              {new Date(event.eventTime).toLocaleString()}
            </DetailRow>
            <DetailRow label="Source">{sourceLabel(event.source)}</DetailRow>
            {event.commerce.orderId && (
              <DetailRow label="Order">{event.commerce.orderId}</DetailRow>
            )}
            {event.commerce.value !== null && (
              <DetailRow label="Value">
                {formatMoney(event.commerce.value, event.commerce.currency)}
                {event.commerce.itemCount > 0
                  ? ` · ${event.commerce.itemCount} item${event.commerce.itemCount === 1 ? "" : "s"}`
                  : ""}
              </DetailRow>
            )}
            {event.identity.email && (
              <DetailRow label="Email">{event.identity.email}</DetailRow>
            )}
            {event.identity.phone && (
              <DetailRow label="Phone">{event.identity.phone}</DetailRow>
            )}
            {event.identity.shopifyCustomerId && (
              <DetailRow label="Customer ID">{event.identity.shopifyCustomerId}</DetailRow>
            )}

            <Box paddingBlockStart="200">
              <Text as="span" variant="bodySm" fontWeight="medium">
                Deliveries
              </Text>
            </Box>
            {event.deliveries.length === 0 ? (
              <Text as="span" variant="bodySm" tone="subdued">
                No deliveries yet. This event may still be in the queue.
              </Text>
            ) : (
              <BlockStack gap="150">
                {event.deliveries.map((d, i) => {
                  const note = deliveryNote(d.status, d.errorMessage);
                  return (
                    <InlineStack
                      key={`${d.destinationType}-${i}`}
                      align="space-between"
                      blockAlign="center"
                    >
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone={note.tone}>
                          {DEST_LABELS[d.destinationType] ?? d.destinationType}
                        </Badge>
                        <Text as="span" variant="bodySm" tone={note.tone === "warning" ? "caution" : "subdued"}>
                          {note.text}
                        </Text>
                      </InlineStack>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {formatRelative(d.attemptedAt)}
                      </Text>
                    </InlineStack>
                  );
                })}
              </BlockStack>
            )}
          </BlockStack>
        </Box>
      </Collapsible>
    </Box>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <InlineStack gap="200" blockAlign="center">
      <div style={{ minWidth: 96 }}>
        <Text as="span" variant="bodySm" tone="subdued">
          {label}
        </Text>
      </div>
      <Text as="span" variant="bodySm">
        {children}
      </Text>
    </InlineStack>
  );
}

// ---------- Formatting ----------

function sourceLabel(source: string): string {
  if (source === "shopify_webhook") return "Webhook";
  if (source === "storefront") return "Pixel";
  return source;
}

function formatMoney(value: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
    }).format(value);
  } catch {
    return `${value} ${currency ?? ""}`.trim();
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
